import type { LogLevel } from '../../config/env.js';
import { ENV } from '../../config/env.js';
import { reportError } from './reportError.js';

/**
 * Numeric priority for log levels (lower = more verbose).
 */
const LOG_LEVEL_PRIORITY: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

/**
 * Structured log payload.
 * PHI must never appear in these payloads.
 */
export interface LogPayload {
  /** Log level */
  level: LogLevel;
  /** Human-readable message (no PHI) */
  message: string;
  /** ISO timestamp */
  timestamp: string;
  /** Optional structured context (no PHI) */
  context?: Record<string, unknown>;
  /** Optional error details (cause is not logged in production) */
  error?: {
    name: string;
    message: string;
    stack?: string;
  };
}

/**
 * Check if a log level should be emitted based on the configured minimum level.
 */
function shouldLog(level: LogLevel): boolean {
  const minPriority = LOG_LEVEL_PRIORITY[ENV.logLevel];
  const currentPriority = LOG_LEVEL_PRIORITY[level];
  return currentPriority >= minPriority;
}

/**
 * Build a structured log payload.
 */
function buildPayload(
  level: LogLevel,
  message: string,
  context?: Record<string, unknown>,
  error?: unknown
): LogPayload {
  const payload: LogPayload = {
    level,
    message,
    timestamp: new Date().toISOString(),
  };

  if (context !== undefined) {
    payload.context = context;
  }

  if (error instanceof Error) {
    payload.error = {
      name: error.name,
      message: error.message,
      // Only include stack in non-production environments
      ...(ENV.isProduction ? {} : { stack: error.stack }),
    };
  }

  return payload;
}

/**
 * Emit a log payload to the console.
 * In a production system, this could also send to a telemetry backend.
 */
function emit(payload: LogPayload): void {
  const { level } = payload;

  // Use appropriate console method based on level
  switch (level) {
    case 'debug':
      console.debug('[DEBUG]', payload);
      break;
    case 'info':
      console.info('[INFO]', payload);
      break;
    case 'warn':
      console.warn('[WARN]', payload);
      break;
    case 'error':
      console.error('[ERROR]', payload);
      break;
    default: {
      // Exhaustive check
      const _exhaustive: never = level;
      console.log('[LOG]', payload, _exhaustive);
    }
  }
}

/**
 * Logger with level-based filtering and structured payloads.
 *
 * @example
 * ```ts
 * logger.info('User logged in', { userId: 'abc123' });
 * logger.error('Failed to fetch data', { endpoint: '/api/users' }, error);
 * ```
 *
 * IMPORTANT: Never include PHI in log messages or context.
 */
export const logger = {
  /**
   * Log a debug message (most verbose).
   */
  debug(message: string, context?: Record<string, unknown>): void {
    if (shouldLog('debug')) {
      emit(buildPayload('debug', message, context));
    }
  },

  /**
   * Log an info message.
   */
  info(message: string, context?: Record<string, unknown>): void {
    if (shouldLog('info')) {
      emit(buildPayload('info', message, context));
    }
  },

  /**
   * Log a warning message.
   */
  warn(message: string, context?: Record<string, unknown>): void {
    if (shouldLog('warn')) {
      emit(buildPayload('warn', message, context));
    }
  },

  /**
   * Log an error message with optional error object.
   */
  error(message: string, context?: Record<string, unknown>, error?: unknown): void {
    if (shouldLog('error')) {
      reportError(error ?? new Error(message), {
        surface: 'logger',
        extra: { message, context },
      });
      emit(buildPayload('error', message, context, error));
    }
  },
} as const;

export type Logger = typeof logger;
